import React, { useState, useEffect } from 'react';
import { Plus, Download, Upload, Copy, Trash2, Edit3, RefreshCw, Globe, Calendar, Layers, AlertCircle } from 'lucide-react';
import DeleteConfirmModal from './DeleteConfirmModal';
import CreateSiteModal from './CreateSiteModal';

const SiteManagementTab = ({ sites, onCreateSite, onDeleteSite, onCloneSite, onExportSite, onImportSite, onSelectSite, isLoading }) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [message, setMessage] = useState(null);

  // Sort sites by last updated
  const sortedSites = [...(sites || [])].sort((a, b) => {
    const dateA = new Date(a.lastUpdated || 0);
    const dateB = new Date(b.lastUpdated || 0);
    return dateB - dateA;
  });

  const handleDelete = (site) => {
    setSelectedSite(site);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (selectedSite) {
      try {
        await onDeleteSite(selectedSite.id);
        setMessage({ type: 'success', text: `Site ${selectedSite.name} deleted successfully` });
        setShowDeleteModal(false);
        setSelectedSite(null);
        setTimeout(() => setMessage(null), 3000);
      } catch (error) {
        setMessage({ type: 'error', text: `Failed to delete site: ${error.message}` });
        setTimeout(() => setMessage(null), 5000);
      }
    }
  };

  const handleClone = async (site) => {
    const newName = prompt(`Enter name for cloned site (cloning from ${site.name}):`);
    if (newName) {
      try {
        await onCloneSite(site.id, newName);
        setMessage({ type: 'success', text: `Site ${site.name} cloned successfully` });
        setTimeout(() => setMessage(null), 3000);
      } catch (error) {
        setMessage({ type: 'error', text: `Failed to clone site: ${error.message}` });
        setTimeout(() => setMessage(null), 5000);
      }
    }
  };

  const handleExport = (site) => {
    try {
      const config = {
        site: site,
        exportDate: new Date().toISOString(),
        version: '1.0.0'
      };
      const blob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${site.id}-config-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setMessage({ type: 'success', text: `Configuration exported for ${site.name}` });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: `Failed to export: ${error.message}` });
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        try {
          const text = await file.text();
          const config = JSON.parse(text);
          await onImportSite(config);
          setMessage({ type: 'success', text: 'Configuration imported successfully' });
          setTimeout(() => setMessage(null), 3000);
        } catch (error) {
          setMessage({ type: 'error', text: `Failed to import: ${error.message}` });
          setTimeout(() => setMessage(null), 5000);
        }
      }
    };
    input.click();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    
    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  const getPlacementCount = (site) => {
    return site.placementCount || 0;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Site Management</h2>
          <p className="text-sm text-gray-600 mt-1">Manage your Prebid sites and configurations</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleImport}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Upload size={16} />
            Import Config
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus size={16} />
            New Site
          </button>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg flex items-center gap-2 ${
          message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
        }`}>
          <AlertCircle size={16} />
          {message.text}
        </div>
      )}

      {/* Sites Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <div className="flex items-center gap-2">
                  <Globe size={14} />
                  Site
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Domain
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <div className="flex items-center gap-2">
                  <Layers size={14} />
                  Placements
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <div className="flex items-center gap-2">
                  <Calendar size={14} />
                  Last Updated
                </div>
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                  <RefreshCw className="animate-spin w-6 h-6 mx-auto mb-2" />
                  Loading sites...
                </td>
              </tr>
            ) : sortedSites.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                  No sites configured. Create your first site to get started.
                </td>
              </tr>
            ) : (
              sortedSites.map((site) => (
                <tr key={site.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{site.name}</div>
                    <div className="text-xs text-gray-500">{site.id}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{site.domain}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{getPlacementCount(site)}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{formatDate(site.lastUpdated)}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => onSelectSite(site.id)}
                        className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Edit Site"
                      >
                        <Edit3 size={16} />
                      </button>
                      <button
                        onClick={() => handleClone(site)}
                        className="p-1.5 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                        title="Clone Site"
                      >
                        <Copy size={16} />
                      </button>
                      <button
                        onClick={() => handleExport(site)}
                        className="p-1.5 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
                        title="Export Configuration"
                      >
                        <Download size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(site)}
                        className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Delete Site"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedSite && (
        <DeleteConfirmModal
          siteName={selectedSite.name}
          onConfirm={confirmDelete}
          onCancel={() => {
            setShowDeleteModal(false);
            setSelectedSite(null);
          }}
        />
      )}

      {/* Create Site Modal */}
      {showCreateModal && (
        <CreateSiteModal
          onCreate={async (siteData) => {
            try {
              await onCreateSite(siteData);
              setMessage({ type: 'success', text: `Site ${siteData.name} created successfully` });
              setShowCreateModal(false);
              setTimeout(() => setMessage(null), 3000);
            } catch (error) {
              setMessage({ type: 'error', text: `Failed to create site: ${error.message}` });
              setTimeout(() => setMessage(null), 5000);
            }
          }}
          onCancel={() => setShowCreateModal(false)}
        />
      )}
    </div>
  );
};

export default SiteManagementTab;